import type {
  Project,
  ProjectDetails,
  Backup,
  Log,
  DashboardStats,
  NotificationSettings,
  ProjectSchedule,
  ScheduleCalendarData,
  ScheduleConflict,
  ScheduleFrequency,
  PluginRelease,
  PluginStats,
  PluginUpdateLog,
  PluginUpdateResult,
  BatchUpdateResult,
  QuarterlyStats,
  ReportRun,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || 'Request failed');
  }

  return response.json();
}

export const api = {
  auth: {
    status: () => fetchApi<{ setupRequired: boolean }>('/auth/status'),

    setup: (password: string) =>
      fetchApi<{ success: boolean }>('/auth/setup', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    login: (password: string) =>
      fetchApi<{ success: boolean }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    logout: () =>
      fetchApi<{ success: boolean }>('/auth/logout', {
        method: 'POST',
      }),

    check: () => fetchApi<{ authenticated: boolean }>('/auth/check'),

    changePassword: (currentPassword: string, newPassword: string) =>
      fetchApi<{ success: boolean }>('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),
  },

  dashboard: {
    getStats: () => fetchApi<DashboardStats>('/dashboard/stats'),
    getActivity: (limit = 20) =>
      fetchApi<Log[]>(`/dashboard/activity?limit=${limit}`),
  },

  projects: {
    list: (params?: {
      page?: number;
      limit?: number;
      status?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
      return fetchApi<{
        projects: Project[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      }>(`/projects?${searchParams}`);
    },

    get: (id: string) => fetchApi<ProjectDetails>(`/projects/${id}`),

    create: (data: { name: string; url: string; apiKey: string }) =>
      fetchApi<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Partial<{ name: string; url: string; apiKey: string; isActive: boolean; kumaMonitorIds: string[]; customerEmail: string | null }>) =>
      fetchApi<Project>(`/projects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi<{ success: boolean }>(`/projects/${id}`, { method: 'DELETE' }),

    testConnection: (id: string) =>
      fetchApi<{ success: boolean; message: string }>(`/projects/${id}/test-connection`, {
        method: 'POST',
      }),

    healthCheck: (id: string) =>
      fetchApi<{ success: boolean; status: string }>(`/projects/${id}/health-check`, {
        method: 'POST',
      }),

    updateConfig: (id: string, config: Record<string, unknown>) =>
      fetchApi<{ success: boolean; message: string }>(`/projects/${id}/config`, {
        method: 'POST',
        body: JSON.stringify(config),
      }),

    getStats: () => fetchApi<{
      total: number;
      ok: number;
      warning: number;
      error: number;
      unknown: number;
    }>('/projects/stats'),
  },

  backups: {
    list: (projectId: string, page = 1, limit = 20) =>
      fetchApi<{
        backups: Backup[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      }>(`/projects/${projectId}/backups?page=${page}&limit=${limit}`),

    get: (projectId: string, backupId: string) =>
      fetchApi<Backup>(`/projects/${projectId}/backups/${backupId}`),

    trigger: (projectId: string) =>
      fetchApi<{ success: boolean; message: string }>(`/projects/${projectId}/backups/trigger`, {
        method: 'POST',
      }),

    delete: (projectId: string, backupId: string) =>
      fetchApi<{ success: boolean }>(`/projects/${projectId}/backups/${backupId}`, {
        method: 'DELETE',
      }),

    getDownloadUrl: (projectId: string, backupId: string) =>
      `${API_BASE}/projects/${projectId}/backups/${backupId}/download`,

    calendar: (projectId: string, year: number, month: number) =>
      fetchApi<{
        year: number;
        month: number;
        days: Record<string, Backup[]>;
      }>(`/projects/${projectId}/backups/calendar?year=${year}&month=${month}`),
  },

  logs: {
    list: (params?: {
      page?: number;
      limit?: number;
      projectId?: string;
      type?: string;
      level?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.limit) searchParams.set('limit', params.limit.toString());
      if (params?.projectId) searchParams.set('projectId', params.projectId);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.level) searchParams.set('level', params.level);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      return fetchApi<{
        logs: Log[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
      }>(`/logs?${searchParams}`);
    },

    getExportUrl: (params?: {
      projectId?: string;
      type?: string;
      level?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.projectId) searchParams.set('projectId', params.projectId);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.level) searchParams.set('level', params.level);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      return `${API_BASE}/logs/export?${searchParams}`;
    },

    stats: () => fetchApi<{
      total: number;
      last24h: number;
      lastWeek: number;
      byType: Record<string, number>;
      byLevel: Record<string, number>;
    }>('/logs/stats'),
  },

  settings: {
    get: () => fetchApi<Record<string, string>>('/settings'),

    getVersion: () =>
      fetchApi<{
        backend: { version: string; commit: string; buildTime: string; changelog: string };
      }>('/version'),

    update: (key: string, value: string, encrypted = false) =>
      fetchApi<{ success: boolean }>(`/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value, encrypted }),
      }),

    getNotifications: () => fetchApi<NotificationSettings>('/settings/notifications'),

    updateNotifications: (data: Partial<NotificationSettings>) =>
      fetchApi<NotificationSettings>('/settings/notifications', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    testEmail: (email: string) =>
      fetchApi<{ success: boolean; message: string }>('/settings/notifications/test', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    testSmtp: () =>
      fetchApi<{ success: boolean; message: string }>('/settings/smtp/test', {
        method: 'POST',
      }),

    testSftp: () =>
      fetchApi<{ success: boolean; message: string }>('/settings/sftp/test', {
        method: 'POST',
      }),

    testKuma: () =>
      fetchApi<{ success: boolean; message: string }>('/settings/kuma/test', {
        method: 'POST',
      }),

    getKumaMonitors: () =>
      fetchApi<{ id: number; name: string; url: string; type: string; active: boolean }[]>(
        '/settings/kuma/monitors'
      ),

    getKumaMonitorStatus: (monitorId: string) =>
      fetchApi<{
        monitorId: number;
        name: string;
        status: 'up' | 'down' | 'pending' | 'maintenance';
        uptime24h: number;
        latency: number;
        lastCheck: string | null;
      }>(`/settings/kuma/monitors/${monitorId}/status`),

    getAllKumaStatus: () =>
      fetchApi<Record<string, {
        monitorId: number;
        name: string;
        status: 'up' | 'down' | 'pending' | 'maintenance';
        uptime24h: number;
        latency: number;
        lastCheck: string | null;
      }>>('/settings/kuma/status'),

    getScheduler: () =>
      fetchApi<{ name: string; schedule: string; lastRun?: string }[]>('/settings/scheduler'),

    runTask: (task: string) =>
      fetchApi<{ success: boolean; message: string }>(`/settings/scheduler/${task}/run`, {
        method: 'POST',
      }),

    // Konfiguration
    getConfig: () =>
      fetchApi<{
        sftp: { host: string; port: number; user: string; password: string; privateKey: string; basePath: string };
        smtp: { host: string; port: number; secure: boolean; user: string; password: string; from: string };
        kuma: { url: string; username: string; password: string };
        scheduler: { healthCheckSchedule: string; weeklyDigestSchedule: string; cleanupSchedule: string; validatorSchedule: string };
        retention: { logDays: number; healthCheckDays: number; screenshotRuns: number };
        health: { phpMinVersion: string; wpMajorVersionThreshold: number; pluginUpdatesWarning: number; themeUpdatesWarning: number };
      }>('/settings/config'),

    updateConfig: (section: string, data: Record<string, string | number | boolean>) =>
      fetchApi<{ success: boolean; message: string }>(`/settings/config/${section}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // GitHub Settings
    getGithubSettings: () =>
      fetchApi<{ tokenConfigured: boolean }>('/settings/github'),

    updateGithubToken: (token: string) =>
      fetchApi<{ success: boolean; message: string }>('/settings/github', {
        method: 'PUT',
        body: JSON.stringify({ token }),
      }),

    deleteGithubToken: () =>
      fetchApi<{ success: boolean; message: string }>('/settings/github', {
        method: 'DELETE',
      }),

    testGithubToken: () =>
      fetchApi<{ success: boolean; message: string }>('/settings/github/test', {
        method: 'POST',
      }),
  },

  schedules: {
    list: (frequency?: ScheduleFrequency) => {
      const params = frequency ? `?frequency=${frequency}` : '';
      return fetchApi<ProjectSchedule[]>(`/schedules${params}`);
    },

    calendar: (year: number, month: number, frequency?: ScheduleFrequency) => {
      const params = new URLSearchParams();
      params.set('year', year.toString());
      params.set('month', month.toString());
      if (frequency) params.set('frequency', frequency);
      return fetchApi<ScheduleCalendarData>(`/schedules/calendar?${params}`);
    },

    conflicts: (year: number, month: number) =>
      fetchApi<ScheduleConflict[]>(`/schedules/conflicts?year=${year}&month=${month}`),

    sync: () =>
      fetchApi<{ synced: number; failed: number }>('/schedules/sync', { method: 'POST' }),
  },

  plugins: {
    getReleases: (pluginSlug: string) =>
      fetchApi<PluginRelease[]>(`/plugins/${pluginSlug}/releases`),

    getStats: (pluginSlug: string) =>
      fetchApi<PluginStats>(`/plugins/${pluginSlug}/stats`),

    createRelease: (pluginSlug: string, formData: FormData) =>
      fetch(`${API_BASE}/plugins/${pluginSlug}/releases`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }).then(async (res) => {
        if (!res.ok) {
          const error = await res.json().catch(() => ({ error: 'Upload failed' }));
          throw new Error(error.error || 'Upload failed');
        }
        return res.json();
      }),

    activateRelease: (pluginSlug: string, version: string) =>
      fetchApi<{ success: boolean; message: string }>(
        `/plugins/${pluginSlug}/releases/${version}/activate`,
        { method: 'PUT' }
      ),

    deactivateRelease: (pluginSlug: string, version: string) =>
      fetchApi<{ success: boolean; message: string }>(
        `/plugins/${pluginSlug}/releases/${version}/deactivate`,
        { method: 'PUT' }
      ),

    deleteRelease: (pluginSlug: string, version: string) =>
      fetchApi<{ success: boolean; message: string }>(
        `/plugins/${pluginSlug}/releases/${version}`,
        { method: 'DELETE' }
      ),

    updateSite: (pluginSlug: string, projectId: string, version: string, createBackup = true) =>
      fetchApi<PluginUpdateResult>(`/plugins/${pluginSlug}/sites/${projectId}/update`, {
        method: 'POST',
        body: JSON.stringify({ version, createBackup }),
      }),

    updateAllSites: (pluginSlug: string, version: string, createBackup = true) =>
      fetchApi<BatchUpdateResult>(`/plugins/${pluginSlug}/update-all`, {
        method: 'POST',
        body: JSON.stringify({ version, createBackup }),
      }),

    rollbackSite: (pluginSlug: string, projectId: string, version?: string) =>
      fetchApi<PluginUpdateResult>(`/plugins/${pluginSlug}/sites/${projectId}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ version }),
      }),

    getPluginInfo: (pluginSlug: string, projectId: string) =>
      fetchApi<{ version: string; isActive: boolean }>(`/plugins/${pluginSlug}/sites/${projectId}/info`),

    getUpdateLogs: (pluginSlug: string, projectId?: string, limit?: number) => {
      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      if (limit) params.set('limit', limit.toString());
      return fetchApi<PluginUpdateLog[]>(`/plugins/${pluginSlug}/update-logs?${params}`);
    },

    syncVersions: (pluginSlug: string) =>
      fetchApi<{ synced: number; failed: number }>(`/plugins/${pluginSlug}/sync-versions`, {
        method: 'POST',
      }),

    syncFromGithub: (pluginSlug: string) =>
      fetchApi<{ success: boolean; message: string; synced: number; latest: string | null; commit: string | null }>(
        `/plugins/${pluginSlug}/sync-github`,
        { method: 'POST' }
      ),

    getGithubInfo: (pluginSlug: string) =>
      fetchApi<{
        owner: string;
        repo: string;
        branch: string;
        url: string;
        latestVersion: string | null;
        latestCommit: string | null;
        latestCommitMessage: string | null;
      }>(`/plugins/${pluginSlug}/github-info`),

    getDownloadUrl: (pluginSlug: string, version: string) =>
      `${API_BASE}/plugins/${pluginSlug}/download/${version}`,
  },

  reports: {
    list: () => fetchApi<ReportRun[]>('/reports'),

    history: (projectId: string) =>
      fetchApi<ReportRun[]>(`/reports/${projectId}/history`),

    preview: (projectId: string, year: number, quarter: number) =>
      fetchApi<QuarterlyStats>(`/reports/${projectId}/preview?year=${year}&quarter=${quarter}`),

    send: (projectId: string, year: number, quarter: number) =>
      fetchApi<{ success: boolean; reportRunId: string; message: string }>(
        `/reports/${projectId}/send`,
        { method: 'POST', body: JSON.stringify({ year, quarter }) }
      ),
  },
};
